import React from 'react';
import { Stack, Tooltip } from '@mui/material';
import styles from './columns.module.scss';
import { startCase } from 'lodash';
import DateUtils from 'js/lib/DateUtils';
import { ChipBadge } from 'new/elements/ChipBadge';
import { Column } from 'new/elements/Table/types';
import { SdkApplication } from '@nexla/@types';
import { ServiceKeysCell } from 'new/components/settings/SdkApplications/ServiceKeysCell';
import { ApplicationIdCell } from 'new/components/settings/SdkApplications/ApplicationIdCell';

export const columns = [
  {
    name: 'Name',
    field: 'name',
    renderRow(row: SdkApplication) {
      return (
        <Stack flexDirection="column" gap="2px">
          <span className={styles.name}>{row.name}</span>
          {row.description && <span className={styles.description}>{row.description}</span>}
        </Stack>
      );
    },
  },
  {
    name: 'Application ID',
    field: 'uuid',
    renderRow(row: SdkApplication) {
      return <ApplicationIdCell uuid={row.uuid} />;
    },
  },
  {
    name: 'Service Keys',
    field: 'id',
    width: 20,
    renderRow(row: SdkApplication) {
      return <ServiceKeysCell app={row} />;
    },
  },
  {
    name: 'Allowed Origins',
    field: 'allowed_origins',
    renderRow(row: SdkApplication) {
      const origins = row.allowed_origins || [];
      if (origins.length === 0) {
        return <span className={styles.emptyState}>—</span>;
      }
      const hidden = origins.slice(2);
      return (
        <Stack flexDirection="row" gap="4px" flexWrap="wrap">
          {origins.slice(0, 2).map((origin) => (
            <ChipBadge key={origin} label={origin} />
          ))}
          {hidden.length > 0 && (
            <Tooltip
              title={
                <Stack flexDirection="column" gap="2px">
                  {hidden.map((origin) => (
                    <span key={origin}>{origin}</span>
                  ))}
                </Stack>
              }
              arrow
              placement="top"
            >
              <span>
                <ChipBadge label={`+${hidden.length} more`} color="disabled" />
              </span>
            </Tooltip>
          )}
        </Stack>
      );
    },
  },
  {
    name: 'Created',
    field: 'created_at',
    renderRow(row: SdkApplication) {
      return DateUtils.convertDateStringToFormat(row.created_at, 'MMMM D, YYYY');
    },
  },
  {
    name: 'Status',
    field: 'status',
    width: 15,
    renderRow(row: SdkApplication) {
      const status = (row.status ?? 'ACTIVE').toUpperCase();
      const isActive = status === 'ACTIVE';
      const isPaused = status === 'PAUSED';
      return (
        <ChipBadge
          label={startCase(status.toLowerCase())}
          dot
          color={isActive ? 'success' : isPaused ? 'warning' : 'disabled'}
        />
      );
    },
  },
] as Column<SdkApplication>[];
